import { useState } from "react";
import { TrendingDown, TrendingUp, Vault, HandCoins, Handshake, Repeat, Gift, Wallet, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { TYPE_STYLES } from "@/lib/txTypes";
import { useInstallments } from "@/lib/useInstallmentsCache";
import { useCommitments } from "@/lib/useCommitmentsCache";
import { LiquidityDetails } from "@/components/dashboard/LiquidityDetails";
import { CounterDetails } from "@/components/dashboard/CounterDetails";

function fmt(n, ccy) {
  return `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${ccy}`;
}

export function NeuCounters({ summary }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const inst = useInstallments();
  const commitmentsData = useCommitments();
  const [showLiq, setShowLiq] = useState(false);
  const [detail, setDetail] = useState(null); // { type, title }
  const ccy = summary?.currency || user?.currency || "AED";
  const tm = summary?.this_month || {};
  const cum = summary?.cumulative || {};
  const derived = summary?.derived || {};
  const st = TYPE_STYLES;

  const activeCount = inst.active?.length || 0;
  const commitmentsCount = (commitmentsData.items || []).filter((c) => c.active !== false).length;
  const recurringTotal = (inst.total_monthly || 0) + (commitmentsData.total_monthly || 0);
  const recurringSub = `${activeCount + commitmentsCount} ${t("activeInstallments")}`;
  const unpaidCommit = (commitmentsData.items || []).some((c) => c.active !== false && c.status !== "paid");
  const unpaidInst = (inst.active || []).some((i) => (i.paid_count || 0) < (i.total_installments || 0));
  const hasDue = unpaidCommit || unpaidInst;
  const tmSavings = Math.max(0, (tm.income || 0) + (tm.revenue || 0) - (tm.expense || 0) - (tm.recurring || 0));
  const open = (type, title) => () => setDetail({ type, title });
  const cards = [
    { key: "income", label: t("income"), value: cum.income ?? 0, sub: `+${fmt(tm.income, "")} · ${t("thisMonth")}`, icon: TrendingUp, bg: st.income.color, testid: "counter-income", onClick: open("income", t("income")) },
    { key: "revenue", label: t("revenue"), value: cum.revenue, sub: `+${fmt(tm.revenue, "")} · ${t("thisMonth")}`, icon: Gift, bg: st.revenue.color, testid: "counter-revenue", onClick: open("revenue", t("revenue")) },
    { key: "expense", label: t("expense"), value: tm.expense, icon: TrendingDown, bg: st.expense.color, testid: "counter-expense", onClick: open("expense", t("expense")) },
    { key: "borrow", label: t("borrow"), value: cum.borrow, sub: `+${fmt(tm.borrow, "")} · ${t("thisMonth")}`, icon: HandCoins, bg: st.borrow.color, testid: "counter-borrow", onClick: open("borrow", t("borrow")) },
    { key: "savings", label: t("savings"), value: cum.savings, sub: `+${fmt(tmSavings, "")} · ${t("thisMonth")}`, icon: Vault, bg: st.savings.color, testid: "counter-savings", onClick: open("savings", t("savings")) },
    { key: "lend", label: t("lend"), value: cum.lend, sub: `+${fmt(tm.lend, "")} · ${t("thisMonth")}`, icon: Handshake, bg: st.lend.color, testid: "counter-lend", onClick: open("lend", t("lend")) },
    { key: "liquidity", label: t("liquidity"), value: derived.liquidity_total ?? derived.liquidity_month, icon: Wallet, bg: "#0f172a", testid: "counter-liquidity", onClick: () => setShowLiq(true) },
    { key: "recurring", label: t("recurring"), value: recurringTotal, sub: recurringSub, icon: Repeat, bg: st.recurring.color, testid: "counter-recurring", onClick: open("recurring", t("recurring")), dot: hasDue },
  ];

  return (
    <>
      <div className="neu p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4">
        {cards.map(({ key, label, value, sub, icon: Icon, bg, testid, onClick, dot }) => (
          <div key={key} data-testid={testid} onClick={onClick}
            className={`neu-sm rounded-2xl p-3 sm:p-4 flex items-center gap-3 relative ${onClick ? "cursor-pointer active:scale-[0.98] transition-transform" : ""}`}>
            {dot && <span data-testid={`${testid}-dot`} className="absolute top-1.5 end-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse" aria-label="due" />}
            <div className="w-10 h-10 rounded-full grid place-items-center shrink-0 shadow-md text-white" style={{ background: bg }}>
              <Icon className="w-5 h-5" strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold truncate flex items-center gap-1">
                {label}
                {onClick && <Info className="w-2.5 h-2.5 opacity-60" />}
              </p>
              <p className="font-display text-base sm:text-lg font-extrabold neu-accent-blue leading-tight tabular-nums truncate">{fmt(value, ccy)}</p>
              {sub && <p className="text-[10px] text-slate-400 truncate">{sub}</p>}
            </div>
          </div>
        ))}
      </div>
      <LiquidityDetails open={showLiq} onClose={() => setShowLiq(false)} />
      <CounterDetails open={!!detail} type={detail?.type} title={detail?.title} onClose={() => setDetail(null)} />
    </>
  );
}
